import type { ComputedRef, MaybeRefOrGetter, ShallowRef } from 'vue';
import { tryOnBeforeUnmount } from '@vueuse/core';
import { createContext } from 'reka-ui';
import { computed, shallowRef, toValue } from 'vue';
import { useTranslationUtils } from '#layers/i18n/public';

type PageBreadcrumbLabel = ComputedRef<string | null | undefined>;

interface PageBreadcrumbItem {
	label: string;
}

const [injectLabels, provideLabels] = createContext<ShallowRef<PageBreadcrumbLabel[]>>('PageSlotProvider', 'PageBreadcrumbLabels');

export function providePageBreadcrumbItems() {
	// Replace the array; in-place mutations aren't tracked.
	provideLabels(shallowRef([]));
}

/**
 * Adds a trailing breadcrumb for a label only the routed page can load, such as a role's name.
 *
 * A nullish or empty label adds no crumb, so a page can pass a name that is still loading.
 * `$t:` placeholders are translated, so a page can pass an item name as stored.
 */
export function definePageBreadcrumbItem(label: MaybeRefOrGetter<string | null | undefined>) {
	const labels = injectLabels(null);
	if (!labels) return;

	// A new computed per call, so unmounting one page can't remove a ref another page also passed.
	const registration = computed(() => toValue(label));
	labels.value = [...labels.value, registration];

	tryOnBeforeUnmount(() => {
		labels.value = labels.value.filter((candidate) => candidate !== registration);
	});
}

export function usePageBreadcrumbItems(): ComputedRef<PageBreadcrumbItem[]> {
	const labels = injectLabels(null);

	if (!labels) return computed(() => []);

	const { translateString } = useTranslationUtils();

	return computed(() => labels.value.flatMap((registration) => {
		const label = translateString(registration.value ?? undefined);
		return label ? [{ label }] : [];
	}));
}
